export const UserType = "User";
export const GroupType = "Group";

/**
 * The feature flags the app knows about (mirrors the backend enum).
 * 
 * NONE is never written to a flag row: it keeps the enum non-empty,
 * which TypeScript requires for the type to be usable when no feature 
 * is behind a flag.
 */
export enum FeatureFlag {
    NONE = "none",
    NEGOTIATION_COACH = "negotiation-coach",
}

/** What an impersonating session is impersonating, as reported by /me. */
export interface Impersonation {
    organizationName?: string;
    organizationDomain?: string;
    username: string;
    impersonator: string;
}

export interface User {
    id: number;
    email: string;
    name: string;
    firstName: string;
    lastName: string;
    avatar: string;
    groups?: Group[];
    type: UGType;
    status: UserStatus;
    roles: UserRole[];
    timezone: string;
    flags?: FeatureFlag[];
    guided: boolean;
    ga?: { [key: string]: any }; // Google Analytics session attrs
    impersonating?: Impersonation; // Present on /me while the session impersonates an org
    /** When the invitation was last sent; set once a user has been invited. */
    invitedAt?: string;
    /** When the user last signed in; unset until their first login. */
    lastLoginAt?: string;
    recordCreationDate?: string;
}

/** One address to invite and the roles it arrives with. */
export interface UserInvite {
    email: string;
    roles: UserRole[];
}

export interface InviteUsersRequest {
    invites: UserInvite[];
}

/**
 * Invites are sent one at a time, so a batch can partly succeed: both halves
 * come back rather than the request failing on the first bad address.
 */
export interface InviteUsersResponse {
    invited: string[];
    failed: { email: string; reason: string }[];
}

export interface Group {
    id: number;
    name: string;
    avatar: string;
    description: string;
    type: UGType;
}

export enum UGType {
    USER = "user",
    GROUP = "group",
}

export enum UserRole {
    USER = "user",
    VIEWER = "viewer",
    ADMIN = "admin",
    // "reviewer" may still appear on historical users; the role is no longer
    // assignable or checked anywhere.
    SUPER_ADMIN = "superadmin",
    BOT = "bot",
    ONBOARDING = "onboarding",
    POC = "poc",
}

export const isSuperAdmin = (roles: UserRole[]) => {
    return roles.indexOf(UserRole.SUPER_ADMIN) !== -1;
};

export const isAdmin = (roles: UserRole[]) => {
    return roles.indexOf(UserRole.ADMIN) !== -1;
};

export const isUser = (roles: UserRole[]) => {
    return roles.indexOf(UserRole.USER) !== -1;
};
export enum UserStatus {
    CREATED = "created",
    WAITING = "waiting",
    ACTIVE = "active",
    INACTIVE = "inactive",
}

export const isOnboarding = (roles?: UserRole[]) => {
    return roles?.includes(UserRole.ONBOARDING) || false;
};

export const isPoc = (roles?: UserRole[]) => {
    return roles?.includes(UserRole.POC) || false;
};

export const isPocOnly = (roles?: UserRole[]) => {
    return !isSuperAdmin(roles ?? []) && isPoc(roles);
};

export const isFeatureFlagEnabled = (user: Partial<User> | undefined, flag: FeatureFlag): boolean => {
    return user?.flags?.includes(flag) ?? false;
}